import { Building2, GraduationCap, Users, Compass, Mail } from "lucide-react";
import { BtnLink, Eyebrow, Reveal, SectionTitle } from "@/components/site/Primitives";
import { PageHero } from "@/components/site/Blocks";
import { OfficeSection } from "@/components/site/OfficeSection";
import { TrustBar } from "@/components/site/TrustBar";
import { CONTACT, useLink, useT } from "@/lib/i18n";

const ICONS = [GraduationCap, Compass, Users, Building2];

export default function Business() {
  const t = useT();
  const b = t.business;
  return (
    <div data-testid="business-page">
      <PageHero eyebrow={b.eyebrow} title={b.title} intro={b.intro} image="/img/ljubljana.jpg" imageAlt={b.imageAlt} />
      {b.trustBar && <TrustBar label={b.trustBar.label} items={b.trustBar.items} />}
      <section className="bg-ivory-2 py-20 sm:py-28">
        <div className="container-x">
          <Reveal><SectionTitle title={b.offerTitle} /></Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {b.offers.map((o, i) => {
              const Icon = ICONS[i];
              return (
                <Reveal key={o.title} delay={i * 0.06}>
                  <div className="card-soft flex h-full gap-6 p-8" data-testid={`business-offer-${i}`}>
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-forest text-gold-light"><Icon size={22} strokeWidth={1.5} /></span>
                    <div>
                      <h3 className="font-display text-[1.7rem] leading-tight text-forest">{o.title}</h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-forest/75">{o.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          {b.offersNote && <p data-testid="business-offers-note" className="mt-10 max-w-3xl text-[15px] leading-relaxed text-forest/75">{b.offersNote}</p>}
        </div>
      </section>
      {b.founder && (
        <section data-testid="business-founder-section" className="container-x pt-20 sm:pt-28">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
            <img
              src="/img/ayse.webp"
              alt={b.founder.alt}
              width="1100"
              height="1005"
              loading="lazy"
              data-testid="business-founder-photo"
              className="mx-auto h-auto w-full max-w-[220px] lg:col-span-5 lg:mx-0 lg:max-w-none"
            />
            <div className="lg:col-span-7">
              <SectionTitle title={b.founder.title} />
              <p data-testid="business-founder-name" className="mt-4 text-base font-medium text-gold-text">{b.founder.name}</p>
              <div className="mt-8 space-y-5 text-base leading-relaxed text-forest/80 sm:text-lg">
                {b.founder.paragraphs.map((p) => <p key={p}>{p}</p>)}
              </div>
            </div>
          </div>
        </section>
      )}
      <section className={`container-x pt-20 sm:pt-28 ${b.faq ? "" : "pb-20 sm:pb-28"}`}>
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><SectionTitle title={b.processTitle} /></Reveal>
            <ol className="mt-10 space-y-6">
              {b.process.map((p, i) => (
                <li key={p} className="flex items-center gap-5 border-t border-forest/15 pt-6">
                  <span className="font-display text-4xl italic text-gold-text">0{i + 1}</span>
                  <span className="text-base text-forest">{p}</span>
                </li>
              ))}
            </ol>
          </div>
          {!b.faq && <CtaCard b={b} className="lg:col-span-7" />}
        </div>
      </section>
      {b.faq && (
        <>
          <section data-testid="business-faq-section" className="container-x pt-20 sm:pt-28">
            <Reveal><SectionTitle title={b.faq.title} /></Reveal>
            <div className="mt-10 max-w-3xl space-y-10">
              {b.faq.items.map((f, i) => (
                <div key={f.q} data-testid={`business-faq-item-${i}`}>
                  <h3 className="text-lg font-semibold leading-snug text-forest">{f.q}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-forest/75">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
          {b.office && <OfficeSection o={b.office} testid="business-office" />}
          <section className="container-x py-20 sm:py-28">
            <CtaCard b={b} />
          </section>
        </>
      )}
    </div>
  );
}

function CtaCard({ b, className }) {
  const link = useLink();
  return (
          <Reveal className={className} delay={0.08}>
            <div className="h-full rounded-[32px] bg-forest p-8 text-ivory sm:p-12">
              <Eyebrow light>{b.eyebrow}</Eyebrow>
              <h2 className="mt-5 font-display text-4xl leading-tight sm:text-5xl">{b.ctaTitle}</h2>
              <p className="mt-5 text-base leading-relaxed text-ivory/85">{b.ctaText}</p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <BtnLink to={link("contact")} variant="ivory" className="hover:!bg-gold-deep" data-testid="business-contact-btn">{b.cta}</BtnLink>
                <a href={`mailto:${CONTACT.email}`} className="btn border border-ivory/30 text-ivory hover:bg-ivory/10" data-testid="business-email-link"><Mail size={16} /> {CONTACT.email}</a>
              </div>
            </div>
          </Reveal>
  );
}
